import get from "lodash/get";
import last from "lodash/last";

import { IRootState } from "../../rootReducer";
import { ActiveInstance, InstanceType } from "../../../utils/Instance/types";
import { IFolder } from "./IFolder";
import { IBookInfo } from "./IBookInfo";
import { IActiveFolderItems } from "./IActiveFolderItems";

import { ROOT_FOLDER } from "./constants";

import { getModalProps } from "../modal/selectors";
import { getIsEdit } from "../display/selectors";
import { getUser } from "../session/selectors";

export function getIsRootExist(state: IRootState): boolean {
  return Boolean(state.library.folders[ROOT_FOLDER]);
}

export function getActiveFolderId(state: IRootState) {
  const { library: { activeFolder, folders } } = state;

  return activeFolder && get(folders, [activeFolder, "id"]);
}

export function findFolderById(state: IRootState, folderId: string): IFolder | undefined {
  return state.library.folders[folderId];
}

export function findBookInfoById(state: IRootState, bookId: string): IBookInfo | undefined {
  return state.library.books[bookId];
}

export function getActiveFolder({ library: { activeFolder } }: IRootState): string | undefined {
  return activeFolder;
}

export const getFolders = ({ library: { folders } }: IRootState) => folders;

export function getActiveRecord({ library: { activeRecord } }: IRootState): string | undefined {
  return activeRecord;
}

export const hasActiveRecord = (state: IRootState) => Boolean(getActiveRecord(state));

export function getActiveRecordContent(state: IRootState): IBookInfo | undefined {
  const { library: { activeRecord, books } } = state;

  return activeRecord ? books[activeRecord] : undefined;
}

export function getActiveRecordText(state: IRootState): string | undefined {
  const { library: { activeRecord, books } } = state;

  return activeRecord && books[activeRecord]
    ? books[activeRecord].text
    : undefined;
}

export function getHasRecordOrigin(state: IRootState): boolean {
  const { library: { activeRecord, books } } = state;

  if (!activeRecord) {
    return false;
  }

  const bookType = get(books, [activeRecord, "bookType"]);

  return bookType
    ? ["FILE", "PDF"].includes(bookType.toUpperCase())
    : false;
}

export function getActiveFolderItems(state: IRootState): IActiveFolderItems | undefined {
  const activeFolderId = state.library.activeFolder;

  if (!activeFolderId) {
    return undefined;
  }

  const folder = findFolderById(state, activeFolderId);

  if (!folder || !folder.isContentLoaded) {
    return undefined;
  }

  const folders: Record<string, IFolder> = {};
  const records: Record<string, IBookInfo> = {};

  Object.keys(folder.folders || {}).forEach((folderId) => {
    const foundFolder = findFolderById(state, folderId);

    if (!foundFolder) {
      console.error(`Something went wrong: app cannot find the folder ${folderId} in the app state`);
      return;
    }
    // doesn't show folder without title
    if (!foundFolder.title) {
      console.error(`Folder with ID ${folderId} has not title.`);
      return;
    }

    // doesn't show archived folder
    if (foundFolder.isArchived) {
      return;
    }

    folders[folderId] = foundFolder;
  });

  Object.keys(folder.books || {}).forEach((bookId) => {
    const foundBook = findBookInfoById(state, bookId);

    if (!foundBook) {
      console.error(`Something went wrong: app cannot find the book ${bookId} in the app state`);
      return;
    }
    // doesn't show record without title
    if (!foundBook.title) {
      console.error(`Record with ID ${bookId} has not title.`);
      return;
    }

    // doesn't show archived folder
    if (foundBook.isArchived) {
      return;
    }

    records[bookId] = foundBook;
  });

  return { folders, records, error: folder.error };
}

export function getActiveFolderTitle(state: IRootState): string | undefined {
  const { library: { activeFolder, folders } } = state;

  return activeFolder && folders[activeFolder]
    ? folders[activeFolder].title
    : undefined;
}

export function getActiveFolderParent(state: IRootState): string | undefined {
  const { library: { activeFolder, folders } } = state;

  return activeFolder && last(get(folders, [activeFolder, "path"]));
}

export function getActiveInstance(state: IRootState) {
  const { id, type } = getModalProps(state) as ActiveInstance;

  switch (type) {
    case InstanceType.RECORD: {
      const record = findBookInfoById(state, id);

      return record
        ? { id, type, title: record.title, path: record.path }
        : null;
    }

    case InstanceType.FOLDER: {
      const folder = findFolderById(state, id);

      return folder
        ? { id, type, title: folder.title, path: folder.path }
        : null;
    }

    default: return null;
  }
}

export enum Mode {
  new = "new",
  edit = "edit",
  view = "view",
}

const getMode = (record: ReturnType<typeof getActiveRecordContent>, isEdit: ReturnType<typeof getIsEdit>) => {
  if (!record) return Mode.new;
  return isEdit ? Mode.edit : Mode.view;
};

const getFolderId = (user: ReturnType<typeof getUser>, record: ReturnType<typeof getActiveRecordContent>) => {
  const uid = get(user, "uid", "");
  const parentFolderId = last(get(record, "path")) || "";

  return parentFolderId === ROOT_FOLDER ? uid : parentFolderId;
};

export const getRecordFormData = (state: IRootState) => {
  const activeRecord = getActiveRecord(state);
  const record = getActiveRecordContent(state);
  const isEdit = getIsEdit(state);
  const user = getUser(state);

  return ({
    mode: getMode(record, isEdit),
    recordMetaData: {
      isLoading: Boolean(activeRecord && !record),
      error: get(record, "error"),
      bookType: get(record, "bookType", "TXT"),
      coverImagePath: get(record, "coverImagePath"),
      pdfDocumentPath: get(record, "pdfDocumentPath"),
    },
    initialValues: {
      folderId: getFolderId(user, record),
      recordId: get(record, "id", ""),
      title: get(record, "title", "Title"),
      text: get(record, "text", ""),
    },
  });
};
